
import { API_URL } from "../../../../constants";
import { getAPI } from "../../../../api/frameworksApi";
import { sharedRequest } from "../../../../utils/sharedRequest";
import type { ScopeNode } from "../types/scope";

type ItComplianceScopeNode = ScopeNode;

export const fetchItComplianceOrgScopeTree = async (
  organizationId: string,
): Promise<ItComplianceScopeNode[]> => {
  const url = `${API_URL.IT_COMPLIANCE_SCOPE_TREE}?organizationId=${encodeURIComponent(organizationId)}`;
  const data = await getAPI(url);
  return data?.data ?? data ?? [];
};

interface AccessibleOrgDevice {
  id: string;
  name: string;
  type?: string;
  status?: string;
  ipAddress?: string | null;
  hostname?: string | null;
  environment?: string | null;
}

interface AccessibleOrgNode {
  id: string;
  name: string;
  shortName?: string | null;
  orgType: string;
  children?: AccessibleOrgNode[];
  devices?: AccessibleOrgDevice[];
}

const ACCESSIBLE_ORG_TYPE_MAP: Record<string, ItComplianceScopeNode["type"]> = {
  GROUP: "group",
  ORGANIZATION: "org",
  DIVISION: "division",
  DEPARTMENT: "department",
  APPLICATION: "application",
};

const mapAccessibleOrgNode = (node: AccessibleOrgNode): ItComplianceScopeNode => {
  const childNodes = (node.children ?? []).map(mapAccessibleOrgNode);
  const deviceNodes: ItComplianceScopeNode[] = (node.devices ?? []).map((device) => ({
    id: `${node.id}/asset:${device.id}`,
    name: device.name,
    type: "asset",
  }));
  const environments = Array.from(
    new Set((node.devices ?? []).map((d) => d.environment).filter((e): e is string => !!e)),
  );
  return {
    id: node.id,
    name: node.name,
    type: ACCESSIBLE_ORG_TYPE_MAP[node.orgType] ?? "org",
    appCode: node.shortName ?? null,
    environments: environments.length ? environments : undefined,
    children: [...childNodes, ...deviceNodes],
  };
};

export const fetchAccessibleOrganizationsTree = async (force = false): Promise<ItComplianceScopeNode[]> => {
  // Several components on one page ask for this tree at once; share a single call between them.
  const data = await sharedRequest(
    "accessible-organizations-tree",
    () => getAPI(API_URL.IT_COMPLIANCE_ACCESSIBLE_ORG_TREE),
    5000,
    force,
  );
  const organizations: AccessibleOrgNode[] = data?.data?.organizations ?? [];
  return organizations.map(mapAccessibleOrgNode);
};
